import { API_BASE_URL } from "../config/apiConfig";

export enum ThumbnailStatus {
    Pending = "pending",
    Completed = "completed",
    Failed = "failed",
    None = "none"
}

export enum ThumbnailCategory {
    Image = "image",
    Audio = "audio",
    Video = "video",
    DocumentText = "document_text",
    DocumentSheet = "document_sheet",
    DocumentHtml = "document_html",
    Epub = "epub",
    Archive = "archive",
    Individual = "individual",
    Other = "other"
}

/**
 * Thumbnail class represents the thumbnail associated to a layer or an entity
 * It contains the file thumbnail ID, URL of the thumbnail, and its status (pending, completed, failed, none).
 */
export default class Thumbnail {
    file_thumbnail_id: number;
    url: string;
    status: ThumbnailStatus;
    category: ThumbnailCategory;

    constructor(file_thumbnail_id: number = 0, url: string = "", status: ThumbnailStatus = ThumbnailStatus.None, category: ThumbnailCategory = ThumbnailCategory.Other) {
        this.file_thumbnail_id = file_thumbnail_id;
        this.url = url;
        this.status = status;
        this.category = category;
    }

    getPath = (): string => {
        // Case 1 : We have a thumbnail structure, but no thumbnail is available.
        // We use category for default thumbnail selection
        if (this.status === ThumbnailStatus.None) {
            return resolveThumbnailPathFromCategory(this.category);
        }
        // Case 2 : Thumbnail is available
        else {
            return resolveThumbnailPath(this.status, this.url, this.category);
        }
    }
}

/**
 * Interface for layers and entities that have a thumbnail.
 * It defines methods to get the thumbnail path and status.
 */
export interface WithThumbnail {
    getThumbnailPath(): string;
    getThumbnailStatus(): ThumbnailStatus;
}

/**
 * Resolves the thumbnail path based on the thumbnail status, URL, and media type.
 * @param status - The status of the thumbnail (pending, completed, failed, none)
 * @param url - The URL of the thumbnail (if available)
 * @param mediaType - The media type of the document (used for default thumbnail selection)
 * @returns string - The resolved thumbnail path
 */
export function resolveThumbnailPath(
    status: ThumbnailStatus,
    url: string,
    category: ThumbnailCategory
): string {
    switch (status) {
        case ThumbnailStatus.Completed:
            return url ? API_BASE_URL + url : resolveThumbnailPathFromCategory(category);
        case ThumbnailStatus.Failed:
            return "/assets/warning.png";
        case ThumbnailStatus.Pending:
            return resolveThumbnailPathFromCategory(category);
        case ThumbnailStatus.None:
        default:
            // Default thumbnail based on media type when no thumbnail is available
            return resolveThumbnailPathFromCategory(category);
    }
}

/**
 * Resolves the thumbnail path based on the media type category.
 * This function is used when no thumbnail is available to provide a default thumbnail based on the media type.
 * @param category - The media type category (e.g., image, audio, video, document_text, etc.)
 * @returns string - The path to the default thumbnail image corresponding to the media type category
 */
export function resolveThumbnailPathFromCategory(
    category: ThumbnailCategory
): string {
    switch (category) {
        case ThumbnailCategory.Image:
            return "/assets/img.png";
        case ThumbnailCategory.Audio:
            return "/assets/audio.png";
        case ThumbnailCategory.Video:
            return "/assets/multimedia.png";
        case ThumbnailCategory.DocumentText:
            return "/assets/text-format.png";
        case ThumbnailCategory.DocumentSheet:
            return "/assets/spreadsheet.png";
        case ThumbnailCategory.DocumentHtml:
            return "/assets/html.png";
        case ThumbnailCategory.Epub:
            return "/assets/epub.png";
        case ThumbnailCategory.Archive:
            return "/assets/archive.png";
        case ThumbnailCategory.Individual:
            return "/assets/user.png";
        case ThumbnailCategory.Other:
        default:
            return "/assets/unknown.png";
    }
}